import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { apiList } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { Comissao } from "@/hooks/useComissoes";
import { toLocalISODate } from "@/lib/date-utils";

export interface ComissaoDetalhada {
  id: string;
  dentista_id: string;
  dentista_nome: string;
  referencia: string;
  data_inicio: string;
  data_fim: string;
  valor_total_procedimentos: number;
  percentual_comissao: number;
  valor_comissao: number;
  status: string;
  data_pagamento: string | null;
  forma_pagamento: string | null;
}

export interface ResumoComissoes {
  total_pago: number;
  total_pendente: number;
  total_geral: number;
  quantidade_paga: number;
  quantidade_pendente: number;
}

export const useRelatorioComissao = (dataInicio?: Date, dataFim?: Date) => {
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();
  const [loading, setLoading] = useState(true);
  const [comissoes, setComissoes] = useState<ComissaoDetalhada[]>([]);
  const [resumo, setResumo] = useState<ResumoComissoes>({
    total_pago: 0,
    total_pendente: 0,
    total_geral: 0,
    quantidade_paga: 0,
    quantidade_pendente: 0,
  });

  const fetchRelatorioComissao = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const [dentistas, comissoesData] = await Promise.all([
        apiList<{ id: string; nome: string }>("dentistas"),
        apiList<Comissao>("comissoes"),
      ]);
      const inicioIso = dataInicio ? toLocalISODate(dataInicio) : "";
      const fimIso = dataFim ? toLocalISODate(dataFim) : "";

      const filtradas = comissoesData.filter((comissao) => {
        if (inicioIso && String(comissao.data_fim || "") < inicioIso) return false;
        if (fimIso && String(comissao.data_inicio || "") > fimIso) return false;
        return true;
      });

      const detalhadas: ComissaoDetalhada[] = filtradas.map((comissao) => {
        const dentista = dentistas.find((item) => item.id === comissao.dentista_id);
        return {
          id: comissao.id,
          dentista_id: comissao.dentista_id,
          dentista_nome: dentista?.nome || "Dentista não encontrado",
          referencia: comissao.referencia,
          data_inicio: comissao.data_inicio,
          data_fim: comissao.data_fim,
          valor_total_procedimentos: Number(comissao.valor_total_procedimentos || 0),
          percentual_comissao: Number(comissao.percentual_comissao || 0),
          valor_comissao: Number(comissao.valor_comissao || 0),
          status: comissao.status,
          data_pagamento: comissao.data_pagamento || null,
          forma_pagamento: comissao.forma_pagamento || null,
        };
      }).sort((a, b) => String(b.data_inicio).localeCompare(String(a.data_inicio)));

      const pagas = detalhadas.filter((item) => item.status === "Paga");
      const pendentes = detalhadas.filter((item) => item.status === "Pendente");

      setComissoes(detalhadas);
      setResumo({
        total_pago: pagas.reduce((sum, item) => sum + item.valor_comissao, 0),
        total_pendente: pendentes.reduce((sum, item) => sum + item.valor_comissao, 0),
        total_geral: detalhadas.reduce((sum, item) => sum + item.valor_comissao, 0),
        quantidade_paga: pagas.length,
        quantidade_pendente: pendentes.length,
      });
    } catch (error: any) {
      console.error("Erro ao buscar relatório de comissões:", error);
      toast.error("Erro ao carregar relatório de comissões");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelatorioComissao();
  }, [user?.id, activeClinicaId, dataInicio?.getTime(), dataFim?.getTime()]);

  return {
    comissoes,
    resumo,
    loading,
    refetch: fetchRelatorioComissao,
  };
};
